import React, { useState, useMemo, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { useAuth0 } from "@auth0/auth0-react";
import {
  Typography,
  Button,
  Box,
  Slide,
  CircularProgress,
  Container,
  TextField,
  InputAdornment,
} from "@mui/material";
import { blue, green, grey } from "@mui/material/colors";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import GavelIcon from "@mui/icons-material/Gavel";
import SecurityIcon from "@mui/icons-material/Security";
import PrivacyTipIcon from "@mui/icons-material/PrivacyTip";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import SearchIcon from "@mui/icons-material/Search";
import AddAdminDialog from "./AdminComponents/AddAdminDialog";
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";
import ConfirmSnackbar from "../../../components/Snackbar/ConfirmSnackbar";
import ReusableTable from "../../../components/ReusableTable/ReusableTable";
import { GET_STAFF_DETAILS } from "../../../apis/organization-apis/people-apis/peopleApis";
import { GET_ROLES } from "../../../apis/dashboard-apis/dashboardDetailApi";
import {
  GET_ROLE_ID_BY_DESCRIPTION,
  GET_ROLE_NAME_BY_DESCRIPTION,
} from "../../../apis/settingsApis/admin-apis/adminApis";
import AdminTableCell from "./AdminComponents/AdminTableCell";
import { useAdminOperations } from "./AdminHooks/useAdminOperations";
import RoleDropdown from "./AdminComponents/RoleDropdown";
import Role from "../../../utils/roles";
import { useLazyQuery } from "@apollo/client";
import { GET_EMPLOYEE_PROFILE_METADATA } from "../../../apis/organization-apis/people-apis/peopleApis";
import { GET_FILES_BY_TYPE } from "../../../apis/common-apis/fileApi";
import InformationStepsCard from "../../../components/InformationStepsCard";
import useAppStore from "../../../stores";
import { roleMatchesFrameworkFilter } from "../../../utils/staffRoleFrameworkFilter";

const transformEmployeeData = (employees) => {
  return employees.map((employee) => ({
    id: employee.employee_id,
    name: employee.name,
    email: employee.emails?.find((e) => e.type === "work")?.email || "",
    staff_role: employee.employments?.[0]?.job_title || "No Role",
    avatar: employee.avatar,
    roles: employee.roles || [],
    is_admin: employee.roles?.find((e) => e?.role_description === Role.ADMIN),
    in_scope: employee.in_scope !== false, // Default to true if not specified
  }));
};

const getAssignedUserForRole = (roleId, admins) => {
  return admins.find((admin) =>
    admin.roles.some((role) => parseInt(role.role_id) === roleId)
  );
};

const AdminList = () => {
  const { loading, error, data, refetch } = useQuery(GET_STAFF_DETAILS);
  const { loading: peopleOpsLoading, data: peopleOpsData } = useQuery(
    GET_ROLE_ID_BY_DESCRIPTION,
    {
      variables: {
        role_description: Role?.PEOPLE_OPERATIONS,
      },
    }
  );

  const { data: peopleOpsNameData } = useQuery(GET_ROLE_NAME_BY_DESCRIPTION, {
    variables: {
      role_description: Role?.PEOPLE_OPERATIONS,
    },
  });

  const { loading: complianceLoading, data: complianceData } = useQuery(
    GET_ROLE_ID_BY_DESCRIPTION,
    {
      variables: {
        role_description: Role?.COMPLIANCE_PROGRAM_MANAGER,
      },
    }
  );

  const { data: complianceNameData } = useQuery(GET_ROLE_NAME_BY_DESCRIPTION, {
    variables: {
      role_description: Role?.COMPLIANCE_PROGRAM_MANAGER,
    },
  });

  const { loading: infosecLoading, data: infosecData } = useQuery(
    GET_ROLE_ID_BY_DESCRIPTION,
    {
      variables: {
        role_description: Role?.INFORMATION_SECURITY_OFFICER,
      },
    }
  );

  const { data: infosecNameData } = useQuery(GET_ROLE_NAME_BY_DESCRIPTION, {
    variables: {
      role_description: Role?.INFORMATION_SECURITY_OFFICER,
    },
  });

  const { loading: consentMgrLoading, data: consentMgrData } = useQuery(
    GET_ROLE_ID_BY_DESCRIPTION,
    {
      variables: {
        role_description: Role?.CONSENT_MANAGER,
      },
    }
  );

  const { data: consentMgrNameData } = useQuery(GET_ROLE_NAME_BY_DESCRIPTION, {
    variables: {
      role_description: Role?.CONSENT_MANAGER,
    },
  });

  const { loading: dpoLoading, data: dpoData } = useQuery(
    GET_ROLE_ID_BY_DESCRIPTION,
    {
      variables: {
        role_description: Role?.DATA_PRIVACY_OFFICER,
      },
    }
  );

  const { data: dpoNameData } = useQuery(GET_ROLE_NAME_BY_DESCRIPTION, {
    variables: {
      role_description: Role?.DATA_PRIVACY_OFFICER,
    },
  });

  const frameworks = useAppStore((state) => state.frameworks);
  const subscribedFrameworkKeys = useMemo(
    () => (frameworks || []).filter((f) => f?.is_active).map((f) => f?.key),
    [frameworks]
  );

  const { data: rolesData } = useQuery(GET_ROLES);
  const [getRoleId] = useLazyQuery(GET_ROLE_ID_BY_DESCRIPTION);
  const [isOpen, setIsOpen] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [openRemoveRoleDialog, setOpenRemoveRoleDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [roleToRemove, setRoleToRemove] = useState(null);
  const [snackbarInfo, setSnackbarInfo] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [linkedInMap, setLinkedInMap] = useState({});
  const [resumeMap, setResumeMap] = useState({});
  const [resumeMapLoaded, setResumeMapLoaded] = useState(false);
  const [getMetadata, { loading: metadataLoading, refetch: refetchMetadata }] =
    useLazyQuery(GET_EMPLOYEE_PROFILE_METADATA);
  const [getFilesByType, { loading: resumeLoading }] =
    useLazyQuery(GET_FILES_BY_TYPE);

  const employees = useMemo(
    () =>
      data?.getEmployees?.data
        ? transformEmployeeData(data.getEmployees.data)
        : [],
    [data]
  );
  const admins = useMemo(
    () => employees.filter((e) => e.is_admin && e.in_scope !== false),
    [employees]
  );

  const nonAdmins = employees.filter((employee) => !employee.is_admin && employee.in_scope !== false);

  const filteredAdmins = admins?.filter((admin) => {
    const searchLower = searchQuery.toLowerCase();
    return (
      admin.name?.toLowerCase().includes(searchLower) ||
      admin.email?.toLowerCase().includes(searchLower) ||
      admin.staff_role?.toLowerCase().includes(searchLower)
    );
  });

  const TransitionComponent = (props) => <Slide {...props} direction="left" />;

  const {
    loadAdmin,
    assignRoleLoading,
    assigningRoleId,
    handleAddAdmin,
    handleRemoveSpecificRole,
    handleConfirmRemove,
    handleAssignRole,
    deleteRolesLoading,
  } = useAdminOperations(refetch, setSnackbarInfo, TransitionComponent);

  const AVAILABLE_ROLES = useMemo(() => {
    if (
      !peopleOpsData?.getRoleIdByDescription?.role_id ||
      !complianceData?.getRoleIdByDescription?.role_id ||
      !infosecData?.getRoleIdByDescription?.role_id
    ) {
      return {};
    }

    const dynamicRoles = {};

    const roleEntries = {
      [Role?.PEOPLE_OPERATIONS]: peopleOpsData?.getRoleIdByDescription,
      [Role?.COMPLIANCE_PROGRAM_MANAGER]:
        complianceData?.getRoleIdByDescription,
      [Role?.INFORMATION_SECURITY_OFFICER]:
        infosecData?.getRoleIdByDescription,
      [Role?.CONSENT_MANAGER]: consentMgrData?.getRoleIdByDescription,
      [Role?.DATA_PRIVACY_OFFICER]: dpoData?.getRoleIdByDescription,
    };

    const ROLE_DESCRIPTIONS = {
      [Role?.PEOPLE_OPERATIONS]:
        "Person responsible for HR or people-related practices and processes.",
      [Role?.COMPLIANCE_PROGRAM_MANAGER]:
        "Person responsible for coordinating activities for all compliances.",
      [Role?.INFORMATION_SECURITY_OFFICER]:
        "Operating leader responsible for the supervision and management of the infosec program",
      [Role?.CONSENT_MANAGER]:
        "Person responsible for managing consent records and data principal rights.",
      [Role?.DATA_PRIVACY_OFFICER]:
        "Person responsible for overseeing data protection strategy and compliance with privacy regulations.",
    };

    const ROLE_ICONS = {
      [Role?.PEOPLE_OPERATIONS]: <PersonIcon />,
      [Role?.COMPLIANCE_PROGRAM_MANAGER]: <GavelIcon />,
      [Role?.INFORMATION_SECURITY_OFFICER]: <SecurityIcon />,
      [Role?.CONSENT_MANAGER]: <PrivacyTipIcon />,
      [Role?.DATA_PRIVACY_OFFICER]: <VerifiedUserIcon />,
    };

    const NAME_DATA = {
      [Role?.PEOPLE_OPERATIONS]: peopleOpsNameData?.getRoleNameByDescription,
      [Role?.COMPLIANCE_PROGRAM_MANAGER]:
        complianceNameData?.getRoleNameByDescription,
      [Role?.INFORMATION_SECURITY_OFFICER]:
        infosecNameData?.getRoleNameByDescription,
      [Role?.CONSENT_MANAGER]:
        consentMgrNameData?.getRoleNameByDescription,
      [Role?.DATA_PRIVACY_OFFICER]: dpoNameData?.getRoleNameByDescription,
    };

    Object.entries(roleEntries).forEach(([description, entry]) => {
      const id = entry?.role_id;
      if (!id) return;
      if (
        !roleMatchesFrameworkFilter(
          entry?.framework_keys,
          subscribedFrameworkKeys
        )
      ) {
        return;
      }

      const roleName = NAME_DATA[description]?.role_name || description;

      dynamicRoles[id] = {
        id,
        name: roleName,
        description: ROLE_DESCRIPTIONS[description],
        color: blue[700],
        icon: ROLE_ICONS[description],
        adminOnly: true,
        key: description,
      };
    });

    return dynamicRoles;
  }, [
    peopleOpsData,
    complianceData,
    infosecData,
    consentMgrData,
    dpoData,
    peopleOpsNameData,
    complianceNameData,
    infosecNameData,
    consentMgrNameData,
    dpoNameData,
    subscribedFrameworkKeys,
  ]);

  const roleFrameworkKeysByDescription = useMemo(
    () => ({
      [Role?.PEOPLE_OPERATIONS]:
        peopleOpsData?.getRoleIdByDescription?.framework_keys ?? null,
      [Role?.COMPLIANCE_PROGRAM_MANAGER]:
        complianceData?.getRoleIdByDescription?.framework_keys ?? null,
      [Role?.INFORMATION_SECURITY_OFFICER]:
        infosecData?.getRoleIdByDescription?.framework_keys ?? null,
      [Role?.CONSENT_MANAGER]:
        consentMgrData?.getRoleIdByDescription?.framework_keys ?? null,
      [Role?.DATA_PRIVACY_OFFICER]:
        dpoData?.getRoleIdByDescription?.framework_keys ?? null,
    }),
    [peopleOpsData, complianceData, infosecData, consentMgrData, dpoData]
  );

  const onConfirmRemove = async () => {
    try {
      await handleConfirmRemove(selectedMember);
      setOpenConfirmDialog(false);
      setSelectedMember(null);
    } catch (error) {
      console.error("Error removing admin:", error);
    }
  };

  const onConfirmRemoveRole = async () => {
    try {
      const roleDescription =
        roleToRemove?.name ===
          peopleOpsNameData?.getRoleNameByDescription?.role_name
          ? Role.PEOPLE_OPERATIONS
          : roleToRemove?.name ===
            complianceNameData?.getRoleNameByDescription?.role_name
            ? Role.COMPLIANCE_PROGRAM_MANAGER
            : roleToRemove?.name ===
              infosecNameData?.getRoleNameByDescription?.role_name
              ? Role.INFORMATION_SECURITY_OFFICER
              : roleToRemove?.name ===
                consentMgrNameData?.getRoleNameByDescription?.role_name
                ? Role.CONSENT_MANAGER
                : roleToRemove?.name ===
                  dpoNameData?.getRoleNameByDescription?.role_name
                  ? Role.DATA_PRIVACY_OFFICER
                  : null;
      if (!roleDescription) {
        console.error(
          "Could not determine role description for:",
          roleToRemove?.name
        );
        return;
      }

      // Query for the role ID
      const { data: roleIdData } = await getRoleId({
        variables: { role_description: roleDescription },
      });

      const roleId = roleIdData?.getRoleIdByDescription?.role_id;

      if (!roleId) {
        console.error(
          "Could not find role ID for description:",
          roleDescription
        );
        return;
      }

      // Create a role object with the correct ID
      const roleWithId = {
        ...roleToRemove,
        id: roleId,
      };

      await handleRemoveSpecificRole(selectedMember, roleWithId);
      setOpenRemoveRoleDialog(false);
      setRoleToRemove(null);
      setSelectedMember(null);
    } catch (error) {
      console.error("Error removing role:", error);
    }
  };

  useEffect(() => {
    if (!admins || admins.length === 0) return; // Only run when admins are loaded
    const fetchMetadata = async () => {
      const emails = Object.values(AVAILABLE_ROLES)
        .map((role) => getAssignedUserForRole(role.id, admins))
        .filter(Boolean)
        .map((user) => user.email);

      await Promise.all(
        emails.map(async (email) => {
          if (linkedInMap[email]) return;

          try {
            const { data } = await getMetadata({ variables: { email } });
            const linkedInUrl =
              data?.getEmployeeProfileMetadata?.linkedin_url || "";
            setLinkedInMap((prev) => {
              if (prev[email] === linkedInUrl) return prev;
              return {
                ...prev,
                [email]: linkedInUrl,
              };
            });
          } catch (err) {
            console.error("Failed to fetch metadata for", email, err);
          }
        })
      );
    };

    fetchMetadata();
  }, [admins, AVAILABLE_ROLES]); // run when admins change

  // Refetch resumes from backend and update resumeMap

  useEffect(() => {
    if (!admins || resumeMapLoaded) return;

    const fetchResumes = async () => {
      try {
        const { data } = await getFilesByType({
          variables: { fileType: "employee_resume" },
        });
        const map = {};
        if (data?.getFilesByType?.files) {
          data.getFilesByType.files.forEach((file) => {
            if (file.ownerEmployeeId) {
              map[file.ownerEmployeeId] = file;
            }
          });
        }
        setResumeMap(map);
        setResumeMapLoaded(true);
      } catch (err) {
        console.error("Failed to fetch resumes", err);
      }
    };

    fetchResumes();
  }, [admins, resumeMapLoaded]);

  const refetchResumes = async () => {
    try {
      const { data } = await getFilesByType({
        variables: { fileType: "employee_resume" },
        fetchPolicy: "network-only", // Ensure we get fresh data
      });
      const map = {};
      if (data?.getFilesByType?.files) {
        data.getFilesByType.files.forEach((file) => {
          if (file.ownerEmployeeId) {
            map[file.ownerEmployeeId] = file;
          }
        });
      }
      setResumeMap(map);
    } catch (err) {
      // Optionally handle error
      console.error("Failed to refetch resumes", err);
    }
  };

  if (
    loading ||
    peopleOpsLoading ||
    complianceLoading ||
    infosecLoading ||
    consentMgrLoading ||
    dpoLoading
  )
    return (
      <Container
        sx={{
          height: "85vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={40} thickness={4} />
      </Container>
    );

  if (error)
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography color="error" variant="h5">
          Failed to load data
        </Typography>
      </Box>
    );

  const handleRoleChange = (roleId, userId) => {
    const role = AVAILABLE_ROLES[roleId];
    const user = admins?.find((admin) => admin?.id === userId);
    if (role && user) {
      handleAssignRole(role, user, rolesData);
    }
  };

  const handleRoleRemove = (roleId) => {
    const role = AVAILABLE_ROLES[roleId];
    const assignedUser = getAssignedUserForRole(roleId, admins);
    if (role && assignedUser) {
      setSelectedMember(assignedUser);
      setRoleToRemove(role);
      setOpenRemoveRoleDialog(true);
    }
  };

  const tableColumns = [
    {
      label: "Member Name",
      field: "name",
      align: "left",
      width: "25%",
    },
    {
      label: "Email",
      field: "email",
      align: "left",
      width: "22%",
    },
    {
      label: "Staff Role",
      field: "staff_role",
      align: "left",
      width: "13%",
    },
    {
      label: "Admin Roles",
      field: "roles",
      align: "left",
      width: "20%",
    },
    {
      label: "",
      field: "actions",
      align: "left",
      width: "15%",
    },
  ];

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        <InformationStepsCard
          heading="**What should I do on this page?**"
          subtitle="This page helps designated administrators manage user roles and Regodit access permissions across the organization."
          steps={[
            "View all existing employees and their assigned roles within the platform.",
            "Assign predefined security and compliance roles (such as **Compliance Manager**, **Infosec Officer**, or **People Operations Person**) to the appropriate employees.",
            "Update or remove role assignments when responsibilities change or employees leave or move to different functions.",
            "Grant or revoke admin access for users who need elevated permissions to manage settings, data, or configurations.",
            "Review role assignments regularly to ensure only authorized users have administrative or privileged access.",
          ]}
          footerText="💡 Proper role management helps maintain data security, accountability, and compliance with least-privilege access principles."
          collapsible={true}
          defaultExpanded={false}
        />
      </Box>

      <Box sx={{ mb: 4 }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 2,
          }}
        >
          {Object.values(AVAILABLE_ROLES).map((role) => {
            const assignedUser = getAssignedUserForRole(role?.id, admins);
            const isAssigned = assignedUser !== null;
            return (
              <RoleDropdown
                key={role.id}
                role={role}
                admins={admins}
                assignedUser={assignedUser}
                onRoleChange={handleRoleChange}
                assignRoleLoading={assignRoleLoading}
                assigningRoleId={assigningRoleId}
                onRemoveRole={handleRoleRemove}
                deleteRolesLoading={deleteRolesLoading}
                linkedInUrl={linkedInMap[assignedUser?.email] || ""}
                setLinkedInUrl={(url) =>
                  setLinkedInMap((prev) => ({
                    ...prev,
                    [assignedUser.email]: url,
                  }))
                }
                resume={resumeMap[assignedUser?.id] || null}
                setResume={(file) =>
                  setResumeMap((prev) => ({ ...prev, [assignedUser.id]: file }))
                }
                refetchResumes={refetchResumes}
                refetchMetadata={refetchMetadata}
                metadataLoading={metadataLoading}
                sx={{
                  border: "1px",
                  borderStyle: isAssigned ? "solid" : "dashed",
                  borderColor: isAssigned ? green[200] : grey[300],
                  bgcolor: isAssigned ? green[50] : "background.paper",
                  color: isAssigned ? role.color : grey[500],
                }}
              />
            );
          })}
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: "background.paper",
          borderRadius: 1,
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: 3,
            borderBottom: "1px solid",
            borderColor: "divider",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: "text.primary", mb: 0.5 }}
            >
              Team Members
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {admins.length} {admins.length === 1 ? "member" : "members"} with
              admin access
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 2 }}>
            <TextField
              size="small"
              placeholder="Search members..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      fontSize="small"
                      sx={{ color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                width: 240,
                "& .MuiOutlinedInput-root": {
                  bgcolor: "background.paper",
                  "&:hover": {
                    bgcolor: "background.paper",
                  },
                },
              }}
            />
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsOpen(true)}
              sx={{
                bgcolor: blue[700],
                "&:hover": {
                  bgcolor: blue[800],
                },
                px: 2,
                py: 1,
                textTransform: "none",
                fontWeight: 500,
              }}
            >
              Add Admin
            </Button>
          </Box>
        </Box>

        <Box>
          <ReusableTable
            columns={tableColumns}
            data={filteredAdmins}
            renderCell={(row, column) => (
              <AdminTableCell
                row={row}
                column={column}
                onRemoveRole={(role) => {
                  setSelectedMember(row);
                  setRoleToRemove(role);
                  setOpenRemoveRoleDialog(true);
                }}
                onRemoveAdmin={() => {
                  setSelectedMember(row);
                  setOpenConfirmDialog(true);
                }}
                peopleOpsNameData={peopleOpsNameData}
                complianceNameData={complianceNameData}
                infosecNameData={infosecNameData}
                consentMgrNameData={consentMgrNameData}
                dpoNameData={dpoNameData}
                subscribedFrameworkKeys={subscribedFrameworkKeys}
                roleFrameworkKeysByDescription={roleFrameworkKeysByDescription}
              />
            )}
            rowsPerPage={10}
            sx={{
              "& .MuiTableCell-root": {
                py: 1.5,
                px: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
              },
              "& .MuiTableRow-root:hover": {
                bgcolor: "rgba(0, 0, 0, 0.01)",
              },
              "& .MuiTableHead-root": {
                bgcolor: grey[50],
                "& .MuiTableCell-root": {
                  color: "text.secondary",
                  fontWeight: 600,
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  py: 1.5,
                },
              },
            }}
          />
        </Box>
      </Box>

      {/* Dialogs */}
      <AddAdminDialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onAddAdmin={handleAddAdmin}
        staffList={nonAdmins}
        loading={loadAdmin}
        setSelectedStaff={setSelectedStaff}
        selectedStaff={selectedStaff}
      />

      <ConfirmDialog
        open={openRemoveRoleDialog && roleToRemove && selectedMember}
        onClose={() => {
          setOpenRemoveRoleDialog(false);
          setRoleToRemove(null);
          setSelectedMember(null);
        }}
        onConfirm={onConfirmRemoveRole}
        heading={`Remove ${roleToRemove?.name} Role?`}
        content={
          <>
            Are you sure you want to remove the <b>{roleToRemove?.name}</b> role
            from{" "}
            <b>
              {selectedMember?.name}
            </b>
            ? They will still remain as an admin but lose access to this
            specific role.
          </>
        }
        buttonText="Yes, Remove Role"
        loading={deleteRolesLoading}
      />

      <ConfirmDialog
        open={openConfirmDialog && selectedMember}
        onClose={() => {
          setOpenConfirmDialog(false);
          setSelectedMember(null);
        }}
        onConfirm={onConfirmRemove}
        heading="Remove Admin Privileges?"
        content={
          <>
            Are you sure you want to remove{" "}
            <b>
              {selectedMember?.name}
            </b>{" "}
            as admin? They'll still have access to the employee portal but lose
            admin privileges and all admin-only roles.
          </>
        }
        buttonText="Yes, Remove"
        loading={loadAdmin}
      />

      {snackbarInfo && <ConfirmSnackbar snackbarInfo={snackbarInfo} />}
    </Container>
  );
};

export default AdminList;
